import React, { useState } from "react";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { DashboardDataProvider } from "@/context/DashboardDataContext";
import { PricingToggle } from "@/components/Pricing/PricingToggle";
import { PricingCards } from "@/components/Pricing/PricingCards";
import { PricingFAQ } from "@/components/Pricing/PricingFAQ";
import { PricingTrustedBy } from "@/components/Pricing/PricingTrustedBy";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/hooks/useAuth";
import { PublicAuthOverlay } from "@/components/public/PublicAuthOverlay";
import { useTranslation } from "@/context/LanguageContext";

const PricingContent = ({ billingCycle, setBillingCycle }: { billingCycle: "monthly" | "yearly"; setBillingCycle: (v: "monthly" | "yearly") => void }) => {
  const { t } = useTranslation();
  return (
    <main className="flex-1 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-7xl py-8">
        <div className="w-full max-w-6xl mx-auto py-10 px-4">
          <div className="flex flex-col items-center text-center mb-20">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-4">
              {t("pricing.pageTitle")}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg lg:text-xl font-normal max-w-3xl">
              {t("pricing.pageSubtitle")}
            </p>
            <div className="mt-6">
              <PricingToggle billingCycle={billingCycle} onChange={setBillingCycle} />
            </div>
          </div>
          <PricingCards billingCycle={billingCycle} />
          <div className="mt-20 hidden lg:block">
            <PricingTrustedBy />
          </div>
          <div className="mt-20">
            <PricingFAQ />
          </div>
        </div>
      </div>
    </main>
  );
};

export default function Pricing() {
  usePageMeta(
    "Pricing — Shattara AI",
    "Choose the right Shattara AI plan for your learning needs. Free plan available. Upgrade for unlimited AI tutoring, quizzes, and flashcards."
  );
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const { user, loading } = useAuth();
  const isPublicView = !user;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isPublicView) {
    return (
      <DashboardShell isPublicView>
        <PublicAuthOverlay />
        <PricingContent billingCycle={billingCycle} setBillingCycle={setBillingCycle} />
      </DashboardShell>
    );
  }

  return (
    <DashboardDataProvider>
      <DashboardShell>
        <PricingContent billingCycle={billingCycle} setBillingCycle={setBillingCycle} />
      </DashboardShell>
    </DashboardDataProvider>
  );
}
